import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, switchMap, tap, throwError } from 'rxjs';
import { OrgUserApi } from './org-user.api';
import {
  IOrgUserDetail,
  IOrgUserSaveRequest,
  IUserAllocation,
  IUserAllocationOption,
} from './org-user.model';

@Injectable({ providedIn: 'root' })
export class OrgUserStore {
  private readonly api = inject(OrgUserApi);

  private readonly _users = rxResource({
    params: () => ({ limit: 100 }),
    stream: ({ params }) => this.api.getUsers(params),
  });

  readonly users = computed(() => this._users.value()?.results ?? []);
  readonly isLoading = computed(() => this._users.isLoading());
  readonly error = computed(() => this._users.error());

  getUser(id: number): Observable<IOrgUserDetail> {
    return this.api.getUser(id);
  }

  /**
   * Creating a user requires allocating the seat to a plan or product
   * (the API rejects unallocated creates with PLEASE_ALLOCATE_PRODUCT_OR_PLAN).
   * TODO: when several plans/products have free capacity the old app lets the
   * user pick one — for now the first option with a free user seat is used.
   */
  createUser(input: IOrgUserSaveRequest): Observable<IOrgUserDetail> {
    return forkJoin([this.api.getAllocationPlans(), this.api.getAllocationProducts()]).pipe(
      switchMap(([plans, products]) => {
        const allocation = this.pickAllocation(plans, products);
        if (!allocation) {
          return throwError(() => new Error('No plan or product with a free user seat available.'));
        }
        return this.api.createUser(input, allocation);
      }),
      tap(() => this._users.reload()),
    );
  }

  updateUser(id: number, input: IOrgUserSaveRequest): Observable<IOrgUserDetail> {
    return this.api.updateUser(id, input).pipe(tap(() => this._users.reload()));
  }

  deleteUser(id: number): Observable<void> {
    return this.api.deleteUser(id).pipe(tap(() => this._users.reload()));
  }

  private pickAllocation(
    plans: IUserAllocationOption[],
    products: IUserAllocationOption[],
  ): IUserAllocation | null {
    const now = Date.now();
    const product = products.find(
      (p) =>
        p.total_user < p.max_user &&
        (!p.expiration_trigger || Date.parse(p.expiration_trigger) > now),
    );
    if (product) return { product: product.id };

    const plan = plans.find((p) => p.total_user < p.max_user) ?? plans[0];
    return plan ? { plan: plan.id } : null;
  }
}
